import React from "react";
import dayjs from "dayjs";
import { MemberProfile } from "../../../../model/members/MemberProfile";
import { daysLeft, elapsedPct, isExpired, periodStart, EXPIRY_WARN_DAYS } from "./validity";

interface ValidityMeterProps {
    member: MemberProfile;
    // The header variant shows the from/to dates above the track.
    showDates?: boolean;
    style?: React.CSSProperties;
}

// from–to dates, a teal progress track and an "Expires in N days" label. Track
// fill + label turn amber within 30 days of expiry.
const ValidityMeter: React.FC<ValidityMeterProps> = ({ member, showDates = true, style }) => {
    const left = daysLeft(member);
    const pct = elapsedPct(member);
    // Expired by category / legacy status while valid-to is still ahead: the
    // meter says Expired in red rather than counting down (FORT-454).
    const expired = isExpired(member);
    const danger = left <= EXPIRY_WARN_DAYS;
    const tone = expired ? "var(--ft-error)" : danger ? "var(--ft-warning)" : undefined;
    const fmt = (d: Date) => dayjs(d).format("DD MMM YYYY");
    // The meter tracks the current period, so its left label is the period start
    // (start of month / one year back), not the original join date.
    const startLabel = member.validTo ? periodStart(member).format("DD MMM YYYY") : "—";

    return (
        <div style={style}>
            {showDates && (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        color: "var(--ft-muted)",
                        fontWeight: 600,
                        marginBottom: 6,
                    }}
                >
                    <span>{startLabel}</span>
                    <span>{member.validTo ? fmt(member.validTo) : "—"}</span>
                </div>
            )}
            <div className="ft-meter">
                <span style={{ width: `${pct}%`, background: tone ?? "var(--ft-teal)" }} />
            </div>
            <div
                style={{
                    marginTop: 6,
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: tone ?? "var(--ft-teal-dark)",
                }}
            >
                {left < 0 ? `Expired ${Math.abs(left)} days ago` : expired ? "Expired" : `Expires in ${left} days`}
            </div>
        </div>
    );
};

export default ValidityMeter;
